import ReactHtmlParser from "html-react-parser";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";

type TextProperty = {
  type: "string";
  value?: string;
};

type BooleanProperty = {
  type: "bool";
  value: boolean;
};

type ArrayProperty = {
  type: "array";
  value: string[];
};

type HierarchyProperty = {
  type: "hierarchy";
  value: string[];
};

type PropertyType =
  | TextProperty
  | BooleanProperty
  | ArrayProperty
  | HierarchyProperty;

const PropertyItem = (props: PropertyType) => {
  const empty = "(Empty)";

  const renderText = (text?: string, color?: string) => (
    <Typography
      sx={{ display: "inline" }}
      component="span"
      variant="body2"
      color={color || "text.secondary"}
    >
      <Box sx={{ fontStyle: "italic" }}>
        {text ? ReactHtmlParser(text) : empty}
      </Box>
    </Typography>
  );

  const renderBool = (value: boolean) =>
    value ? (
      <CheckIcon fontSize="small" color="success" />
    ) : (
      <ClearIcon fontSize="small" color="warning" />
    );

  const renderHierarchy = (values: string[]) =>
    values.length > 0
      ? renderText(values.join(" &raquo; "))
      : renderText(undefined);

  const renderArray = (values: string[]) =>
    values.length > 0 ? (
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={0.5}
      >
        {values.map((value, index) => (
          <Chip
            label={renderText(value, "primary")}
            size="small"
            variant="outlined"
            color="primary"
            key={index}
          />
        ))}
      </Stack>
    ) : (
      renderText(undefined)
    );

  switch (props.type) {
    case "string":
      return renderText(props.value);
    case "bool":
      return renderBool(!!props.value);
    case "hierarchy":
      return renderHierarchy(props.value);
    case "array":
      return renderArray(props.value);
    default:
      return renderText(undefined);
  }
};

export default PropertyItem;
